import React, { useState, useEffect, useRef } from 'react';
import { 
  BookOpen, Calendar, Plus, Trash2, Edit2, Check, X, 
  TrendingUp, TrendingDown, Clock, AlertCircle, CheckCircle,
  Users, DollarSign, ChevronRight, ArrowUpRight, ArrowDownLeft,
  Phone, MoreVertical, Filter, Search, Bell, Volume2, Upload, Play, Pause
} from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

const SuperTool = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  
  // Account Book State
  const [parties, setParties] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [selectedParty, setSelectedParty] = useState(null);
  const [showAddParty, setShowAddParty] = useState(false);
  const [showAddTransaction, setShowAddTransaction] = useState(false);
  const [partyForm, setPartyForm] = useState({ name: '', phone: '', type: 'customer' });
  const [transactionForm, setTransactionForm] = useState({ party_id: '', amount: '', type: 'credit', note: '' });
  
  // Calendar/Tasks State
  const [tasks, setTasks] = useState([]);
  const [showAddTask, setShowAddTask] = useState(false);
  const [taskForm, setTaskForm] = useState({ 
    title: '', description: '', due_date: '', due_time: '', type: 'task', priority: 'medium',
    alarm_enabled: false, alarm_tone: 'gentle_morning', alarm_minutes_before: 15, custom_tone_url: ''
  });
  const [taskFilter, setTaskFilter] = useState('all');
  
  // Alarm Tones State
  const [alarmTones, setAlarmTones] = useState([]);
  const [playingTone, setPlayingTone] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    fetchSettings();
    fetchAlarmTones();
  }, []);

  useEffect(() => {
    if (settings?.is_enabled) {
      fetchDashboard();
      if (activeTab === 'accounts') fetchParties();
      if (activeTab === 'calendar') fetchTasks();
    }
  }, [activeTab, settings]);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/supertool/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAlarmTones = async () => {
    try {
      const response = await api.get('/supertool/alarm-tones');
      setAlarmTones(response.data.tones || []);
    } catch (error) {
      console.log('Using default tones');
    }
  };

  const playTonePreview = (toneId) => {
    if (playingTone === toneId) {
      audioRef.current?.pause();
      setPlayingTone(null);
      return;
    }
    
    // Use Web Audio API for preview (demo sounds)
    const tone = alarmTones.find(t => t.id === toneId);
    if (tone) {
      // Create a simple preview beep
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Different frequencies for different tones
      const frequencies = {
        'gentle_morning': 440, 'temple_bells': 523, 'crystal_chime': 659,
        'zen_garden': 392, 'morning_birds': 587, 'soft_piano': 349,
        'flute_melody': 494, 'water_drops': 415, 'wind_chimes': 554,
        'om_chant': 261, 'sitar_tune': 466, 'tabla_beat': 311,
        'conch_shell': 233, 'aarti_bells': 622, 'classic_alarm': 880,
        'digital_beep': 1000, 'notification_pop': 700, 'success_chime': 784,
        'marimba': 523, 'harp_gliss': 698
      };
      
      oscillator.frequency.value = frequencies[toneId] || 440;
      oscillator.type = tone.category === 'soft' ? 'sine' : tone.category === 'spiritual' ? 'triangle' : 'square';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.8);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.8);
      
      setPlayingTone(toneId);
      setTimeout(() => setPlayingTone(null), 800);
    }
  };

  const fetchDashboard = async () => {
    try {
      const response = await api.get('/supertool/dashboard');
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    }
  };

  const fetchParties = async () => {
    try {
      const response = await api.get('/supertool/parties');
      setParties(response.data);
    } catch (error) {
      toast.error('Failed to fetch parties');
    }
  };

  const fetchTransactions = async (partyId) => {
    try {
      const response = await api.get(`/supertool/transactions/party/${partyId}`);
      setSelectedParty(response.data);
    } catch (error) {
      toast.error('Failed to fetch transactions');
    }
  };

  const fetchTasks = async () => {
    try {
      const response = await api.get('/supertool/tasks');
      setTasks(response.data);
    } catch (error) {
      toast.error('Failed to fetch tasks');
    }
  };

  // Party Actions
  const handleAddParty = async (e) => {
    e.preventDefault();
    try {
      await api.post('/supertool/parties', partyForm);
      toast.success('Party added successfully');
      setShowAddParty(false);
      setPartyForm({ name: '', phone: '', type: 'customer' });
      fetchParties();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add party');
    }
  };

  const handleDeleteParty = async (partyId) => {
    if (!window.confirm('Delete this party and all transactions?')) return;
    try {
      await api.delete(`/supertool/parties/${partyId}`);
      toast.success('Party deleted');
      fetchParties();
      if (selectedParty?.party?.id === partyId) setSelectedParty(null);
    } catch (error) {
      toast.error('Failed to delete party');
    }
  };

  // Transaction Actions
  const handleAddTransaction = async (e) => {
    e.preventDefault();
    try {
      await api.post('/supertool/transactions', {
        ...transactionForm,
        amount: parseFloat(transactionForm.amount)
      });
      toast.success('Transaction added');
      setShowAddTransaction(false);
      setTransactionForm({ party_id: '', amount: '', type: 'credit', note: '' });
      if (selectedParty) fetchTransactions(selectedParty.party.id);
      fetchParties();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add transaction');
    }
  };

  // Task Actions
  const handleAddTask = async (e) => {
    e.preventDefault();
    try {
      await api.post('/supertool/tasks', taskForm);
      toast.success('Task added successfully');
      setShowAddTask(false);
      setTaskForm({ title: '', description: '', due_date: '', due_time: '', type: 'task', priority: 'medium' });
      fetchTasks();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add task');
    }
  };

  const handleToggleTask = async (taskId) => {
    try {
      await api.put(`/supertool/tasks/${taskId}/toggle`);
      fetchTasks();
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await api.delete(`/supertool/tasks/${taskId}`);
      toast.success('Task deleted');
      fetchTasks();
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const filteredTasks = tasks.filter(task => {
    if (taskFilter === 'all') return true;
    if (taskFilter === 'pending') return !task.is_completed;
    if (taskFilter === 'completed') return task.is_completed;
    if (taskFilter === 'today') {
      const today = new Date().toISOString().split('T')[0];
      return task.due_date === today;
    }
    return task.type === taskFilter;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-10 h-10 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!settings?.is_enabled) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="glass-card p-8 text-center max-w-md">
          <div className="w-20 h-20 mx-auto mb-6 bg-yellow-500/10 rounded-full flex items-center justify-center">
            <AlertCircle className="w-10 h-10 text-yellow-500" />
          </div>
          <h1 className="text-2xl font-bold mb-2">SuperTool</h1>
          <p className="text-muted-foreground mb-4">
            SuperTool is currently disabled by admin. Please contact support to enable this feature.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-10 px-4" data-testid="supertool-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">SuperTool</h1>
          <p className="text-muted-foreground">Account Book + Calendar & Tasks</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: 'dashboard', label: 'Dashboard', icon: TrendingUp },
            { id: 'accounts', label: 'Account Book', icon: BookOpen },
            { id: 'calendar', label: 'Calendar & Tasks', icon: Calendar },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-white/5 hover:bg-white/10'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && dashboard && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="glass-card p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
                    <ArrowDownLeft className="w-5 h-5 text-green-500" />
                  </div>
                  <span className="text-sm text-muted-foreground">To Receive</span>
                </div>
                <p className="text-2xl font-bold text-green-500">
                  ₹{dashboard.account_book?.total_to_receive?.toLocaleString() || 0}
                </p>
              </div>

              <div className="glass-card p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center">
                    <ArrowUpRight className="w-5 h-5 text-red-500" />
                  </div>
                  <span className="text-sm text-muted-foreground">To Pay</span>
                </div>
                <p className="text-2xl font-bold text-red-500">
                  ₹{dashboard.account_book?.total_to_pay?.toLocaleString() || 0}
                </p>
              </div>

              <div className="glass-card p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-yellow-500/20 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-yellow-500" />
                  </div>
                  <span className="text-sm text-muted-foreground">Today's Tasks</span>
                </div>
                <p className="text-2xl font-bold">
                  {dashboard.calendar?.today_tasks || 0}
                </p>
              </div>

              <div className="glass-card p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center">
                    <AlertCircle className="w-5 h-5 text-red-500" />
                  </div>
                  <span className="text-sm text-muted-foreground">Overdue</span>
                </div>
                <p className="text-2xl font-bold text-red-500">
                  {dashboard.calendar?.overdue_tasks || 0}
                </p>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                onClick={() => { setActiveTab('accounts'); setShowAddParty(true); }}
                className="glass-card p-5 text-left hover:border-primary/50 transition-all group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
                      <Users className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold">Add New Party</p>
                      <p className="text-sm text-muted-foreground">Customer or Vendor</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
              </button>

              <button
                onClick={() => { setActiveTab('calendar'); setShowAddTask(true); }}
                className="glass-card p-5 text-left hover:border-primary/50 transition-all group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-blue-500" />
                    </div>
                    <div>
                      <p className="font-semibold">Add New Task</p>
                      <p className="text-sm text-muted-foreground">Task or Appointment</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
              </button>
            </div>
          </div>
        )}

        {/* Account Book Tab */}
        {activeTab === 'accounts' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Parties List */}
            <div className="lg:col-span-1">
              <div className="glass-card p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold">Parties ({parties.length})</h3>
                  <button
                    onClick={() => setShowAddParty(true)}
                    className="p-2 bg-primary/20 rounded-lg hover:bg-primary/30 transition-colors"
                    data-testid="add-party-btn"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>

                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {parties.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">
                      No parties yet. Add your first customer or vendor!
                    </p>
                  ) : (
                    parties.map(party => (
                      <div
                        key={party.id}
                        onClick={() => fetchTransactions(party.id)}
                        className={`p-3 rounded-xl cursor-pointer transition-all ${
                          selectedParty?.party?.id === party.id
                            ? 'bg-primary/20 border border-primary/50'
                            : 'bg-white/5 hover:bg-white/10'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{party.name}</p>
                            <p className="text-xs text-muted-foreground capitalize">{party.type}</p>
                          </div>
                          <p className={`font-bold ${party.balance >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {party.balance >= 0 ? '+' : ''}₹{Math.abs(party.balance).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Transactions */}
            <div className="lg:col-span-2">
              {selectedParty ? (
                <div className="glass-card p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-lg">{selectedParty.party.name}</h3>
                      <p className={`text-sm ${selectedParty.current_balance >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        Balance: ₹{Math.abs(selectedParty.current_balance).toLocaleString()}
                        {selectedParty.current_balance >= 0 ? ' (To Receive)' : ' (To Pay)'}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setTransactionForm({ ...transactionForm, party_id: selectedParty.party.id });
                          setShowAddTransaction(true);
                        }}
                        className="px-3 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium"
                        data-testid="add-transaction-btn"
                      >
                        <Plus className="w-4 h-4 inline mr-1" />
                        Add Entry
                      </button>
                      <button
                        onClick={() => handleDeleteParty(selectedParty.party.id)}
                        className="p-2 bg-red-500/20 text-red-500 rounded-lg hover:bg-red-500/30"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {selectedParty.transactions.length === 0 ? (
                      <p className="text-center text-muted-foreground py-8">
                        No transactions yet
                      </p>
                    ) : (
                      selectedParty.transactions.map(trans => (
                        <div key={trans.id} className="p-3 bg-white/5 rounded-xl">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                trans.type === 'credit' ? 'bg-green-500/20' : 'bg-red-500/20'
                              }`}>
                                {trans.type === 'credit' ? (
                                  <ArrowUpRight className="w-4 h-4 text-green-500" />
                                ) : (
                                  <ArrowDownLeft className="w-4 h-4 text-red-500" />
                                )}
                              </div>
                              <div>
                                <p className="font-medium">
                                  {trans.type === 'credit' ? 'You Gave' : 'You Got'}
                                </p>
                                {trans.note && (
                                  <p className="text-xs text-muted-foreground">{trans.note}</p>
                                )}
                                <p className="text-xs text-muted-foreground">
                                  {new Date(trans.date).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className={`font-bold ${trans.type === 'credit' ? 'text-green-500' : 'text-red-500'}`}>
                                {trans.type === 'credit' ? '-' : '+'}₹{trans.amount.toLocaleString()}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                Bal: ₹{trans.running_balance?.toLocaleString()}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              ) : (
                <div className="glass-card p-8 text-center">
                  <BookOpen className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground">Select a party to view transactions</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Calendar/Tasks Tab */}
        {activeTab === 'calendar' && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex gap-2 overflow-x-auto">
                {['all', 'pending', 'completed', 'today', 'task', 'appointment'].map(filter => (
                  <button
                    key={filter}
                    onClick={() => setTaskFilter(filter)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium capitalize transition-all ${
                      taskFilter === filter
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-white/5 hover:bg-white/10'
                    }`}
                  >
                    {filter}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setShowAddTask(true)}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-xl font-medium"
                data-testid="add-task-btn"
              >
                <Plus className="w-4 h-4" />
                Add Task
              </button>
            </div>

            <div className="space-y-3">
              {filteredTasks.length === 0 ? (
                <div className="glass-card p-8 text-center">
                  <Calendar className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground">No tasks found</p>
                </div>
              ) : (
                filteredTasks.map(task => (
                  <div key={task.id} className={`glass-card p-4 ${task.is_completed ? 'opacity-60' : ''}`}>
                    <div className="flex items-start gap-3">
                      <button
                        onClick={() => handleToggleTask(task.id)}
                        className={`mt-1 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                          task.is_completed
                            ? 'bg-green-500 border-green-500'
                            : 'border-muted-foreground hover:border-primary'
                        }`}
                      >
                        {task.is_completed && <Check className="w-3 h-3 text-white" />}
                      </button>
                      
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className={`font-medium ${task.is_completed ? 'line-through' : ''}`}>
                              {task.title}
                            </p>
                            {task.description && (
                              <p className="text-sm text-muted-foreground mt-1">{task.description}</p>
                            )}
                          </div>
                          <button
                            onClick={() => handleDeleteTask(task.id)}
                            className="p-1 text-muted-foreground hover:text-red-500 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        
                        <div className="flex items-center gap-3 mt-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            task.type === 'appointment' ? 'bg-blue-500/20 text-blue-500' : 'bg-primary/20 text-primary'
                          }`}>
                            {task.type}
                          </span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            task.priority === 'high' ? 'bg-red-500/20 text-red-500' :
                            task.priority === 'medium' ? 'bg-yellow-500/20 text-yellow-500' :
                            'bg-gray-500/20 text-gray-500'
                          }`}>
                            {task.priority}
                          </span>
                          {task.alarm_enabled && (
                            <>
                              <span className="px-2 py-0.5 rounded text-xs font-medium bg-yellow-500/20 text-yellow-500 flex items-center gap-1">
                                <Bell className="w-3 h-3" />
                                Alarm
                              </span>
                              <button
                                onClick={() => {
                                  // Dispatch a synthetic alarm event the scheduler listens to
                                  window.dispatchEvent(new CustomEvent('supertool:test-alarm', { detail: task }));
                                }}
                                className="px-2 py-0.5 rounded text-xs font-medium bg-orange-500/20 text-orange-500 hover:bg-orange-500/30 flex items-center gap-1"
                                data-testid={`test-alarm-btn-${task.id}`}
                                title="Test alarm now"
                              >
                                <Play className="w-3 h-3" />
                                Test
                              </button>
                            </>
                          )}
                          {task.due_date && (
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {task.due_date} {task.due_time || ''}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Add Party Modal */}
        {showAddParty && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="glass-card p-6 max-w-md w-full">
              <h3 className="text-xl font-bold mb-4">Add New Party</h3>
              <form onSubmit={handleAddParty} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Name *</label>
                  <input
                    type="text"
                    value={partyForm.name}
                    onChange={(e) => setPartyForm({ ...partyForm, name: e.target.value })}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Phone</label>
                  <input
                    type="tel"
                    value={partyForm.phone}
                    onChange={(e) => setPartyForm({ ...partyForm, phone: e.target.value })}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Type</label>
                  <select
                    value={partyForm.type}
                    onChange={(e) => setPartyForm({ ...partyForm, type: e.target.value })}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                  >
                    <option value="customer">Customer</option>
                    <option value="vendor">Vendor</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowAddParty(false)}
                    className="flex-1 py-2 bg-white/10 rounded-xl font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 py-2 bg-primary text-primary-foreground rounded-xl font-medium"
                  >
                    Add Party
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Add Transaction Modal */}
        {showAddTransaction && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="glass-card p-6 max-w-md w-full">
              <h3 className="text-xl font-bold mb-4">Add Transaction</h3>
              <form onSubmit={handleAddTransaction} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Amount *</label>
                  <input
                    type="number"
                    value={transactionForm.amount}
                    onChange={(e) => setTransactionForm({ ...transactionForm, amount: e.target.value })}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-xl font-bold"
                    placeholder="0"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Type</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setTransactionForm({ ...transactionForm, type: 'credit' })}
                      className={`p-3 rounded-xl font-medium transition-all ${
                        transactionForm.type === 'credit'
                          ? 'bg-red-500 text-white'
                          : 'bg-white/5 hover:bg-white/10'
                      }`}
                    >
                      You Gave (Credit)
                    </button>
                    <button
                      type="button"
                      onClick={() => setTransactionForm({ ...transactionForm, type: 'debit' })}
                      className={`p-3 rounded-xl font-medium transition-all ${
                        transactionForm.type === 'debit'
                          ? 'bg-green-500 text-white'
                          : 'bg-white/5 hover:bg-white/10'
                      }`}
                    >
                      You Got (Debit)
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Note</label>
                  <input
                    type="text"
                    value={transactionForm.note}
                    onChange={(e) => setTransactionForm({ ...transactionForm, note: e.target.value })}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                    placeholder="Optional note"
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowAddTransaction(false)}
                    className="flex-1 py-2 bg-white/10 rounded-xl font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 py-2 bg-primary text-primary-foreground rounded-xl font-medium"
                  >
                    Add Entry
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Add Task Modal */}
        {showAddTask && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="glass-card p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
              <h3 className="text-xl font-bold mb-4">Add New Task</h3>
              <form onSubmit={handleAddTask} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Title *</label>
                  <input
                    type="text"
                    value={taskForm.title}
                    onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <textarea
                    value={taskForm.description}
                    onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                    rows={2}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Due Date</label>
                    <input
                      type="date"
                      value={taskForm.due_date}
                      onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })}
                      className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Due Time</label>
                    <input
                      type="time"
                      value={taskForm.due_time}
                      onChange={(e) => setTaskForm({ ...taskForm, due_time: e.target.value })}
                      className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Type</label>
                    <select
                      value={taskForm.type}
                      onChange={(e) => setTaskForm({ ...taskForm, type: e.target.value })}
                      className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                    >
                      <option value="task">Task</option>
                      <option value="appointment">Appointment</option>
                      <option value="reminder">Reminder</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Priority</label>
                    <select
                      value={taskForm.priority}
                      onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value })}
                      className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                </div>
                
                {/* Alarm Settings */}
                <div className="p-4 bg-white/5 rounded-xl space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Bell className="w-5 h-5 text-yellow-500" />
                      <span className="font-medium">Alarm Reminder</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setTaskForm({ ...taskForm, alarm_enabled: !taskForm.alarm_enabled })}
                      className={`w-12 h-6 rounded-full transition-all ${
                        taskForm.alarm_enabled ? 'bg-yellow-500' : 'bg-white/20'
                      }`}
                    >
                      <div className={`w-5 h-5 rounded-full bg-white transition-transform ${
                        taskForm.alarm_enabled ? 'translate-x-6' : 'translate-x-0.5'
                      }`} />
                    </button>
                  </div>
                  
                  {taskForm.alarm_enabled && (
                    <>
                      <div>
                        <label className="block text-sm text-muted-foreground mb-2">Remind me before</label>
                        <select
                          value={taskForm.alarm_minutes_before}
                          onChange={(e) => setTaskForm({ ...taskForm, alarm_minutes_before: parseInt(e.target.value) })}
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm"
                        >
                          <option value={5}>5 minutes</option>
                          <option value={10}>10 minutes</option>
                          <option value={15}>15 minutes</option>
                          <option value={30}>30 minutes</option>
                          <option value={60}>1 hour</option>
                          <option value={120}>2 hours</option>
                          <option value={1440}>1 day</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm text-muted-foreground mb-2">
                          <Volume2 className="w-4 h-4 inline mr-1" />
                          Alarm Tone
                        </label>
                        <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                          {alarmTones.map(tone => (
                            <div
                              key={tone.id}
                              onClick={() => setTaskForm({ ...taskForm, alarm_tone: tone.id })}
                              className={`p-2 rounded-lg cursor-pointer flex items-center gap-2 text-sm transition-all ${
                                taskForm.alarm_tone === tone.id
                                  ? 'bg-yellow-500/20 border border-yellow-500'
                                  : 'bg-white/5 hover:bg-white/10'
                              }`}
                            >
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); playTonePreview(tone.id); }}
                                className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center"
                              >
                                {playingTone === tone.id ? (
                                  <Pause className="w-3 h-3" />
                                ) : (
                                  <Play className="w-3 h-3" />
                                )}
                              </button>
                              <span className="truncate">{tone.name}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm text-muted-foreground mb-2">
                          <Upload className="w-4 h-4 inline mr-1" />
                          Or use Custom Tone (MP3 URL)
                        </label>
                        <input
                          type="url"
                          value={taskForm.custom_tone_url}
                          onChange={(e) => setTaskForm({ ...taskForm, custom_tone_url: e.target.value })}
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm"
                          placeholder="https://example.com/my-tone.mp3"
                        />
                        <p className="text-xs text-muted-foreground mt-1">Supported: MP3, WAV, OGG, M4A</p>
                      </div>
                    </>
                  )}
                </div>
                
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowAddTask(false)}
                    className="flex-1 py-2 bg-white/10 rounded-xl font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 py-2 bg-primary text-primary-foreground rounded-xl font-medium"
                  >
                    Add Task
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SuperTool;
